import { cn } from "@faire/prototype-playground/lib/utils";
import React from "react";

import { PADX, TILE_HOVER_GROUP, TILE_HOVER_IMAGE } from "../constants";
import { SectionHeader } from "../SectionHeader";
import { type CollectionEntry } from "../types";

interface CollectionsRowProps {
  collections: CollectionEntry[];
  /** Grid width — 4-up by default, 2-up for a sparse (new-brand) storefront,
   * 5-up when a brand has enough real covers to fill a single wide row. Rows
   * are however many the collections fill: 8 covers at 4-up is two rows. */
  columns?: 2 | 4 | 5;
  /** Tile crop. Defaults to a per-width ratio (see COLUMN_ASPECT), which is the
   * right guess when covers are ordinary photography. Set it explicitly when
   * the brand's covers are authored at a known ratio — theirs are 1:1 with the
   * collection name typeset into the middle, and any other crop shaves it. */
  imageAspect?: "square" | "portrait" | "landscape";
  /** Optional section title (default "Collections"). */
  title?: string;
  /**
   * When set, each tile links to `linkFor(collection)` (brand.com → Faire)
   * instead of opening the sign-up gate.
   */
  linkFor?: (collection: CollectionEntry) => string;
  /** "See all" in the header. The link rendered regardless before this existed,
   * so on any surface that didn't pass a handler it was a dead control. */
  onSeeAll?: () => void;
}

const COLUMN_GRID: Record<2 | 4 | 5, string> = {
  2: "grid-cols-2",
  4: "grid-cols-4",
  5: "grid-cols-5",
};

// Tile ratio per grid width. 2-up tiles are twice as wide, so a portrait ratio
// would tower — they get a shorter landscape crop. 5-up is square: brands
// author these covers as squares (they're 1:1 on the CDN, with the collection
// name typeset into the middle), and a 4:5 crop shaves the artwork.
const COLUMN_ASPECT: Record<2 | 4 | 5, string> = {
  2: "aspect-[4/3]",
  4: "aspect-[4/5]",
  5: "aspect-square",
};

const IMAGE_ASPECT = {
  square: "aspect-square",
  portrait: "aspect-[4/5]",
  landscape: "aspect-[4/3]",
} as const;

export const CollectionsRow: React.FC<CollectionsRowProps> = ({
  collections,
  columns = 4,
  title = "Collections",
  imageAspect,
  linkFor,
  onSeeAll,
}) => {
  if (collections.length === 0) return null;
  return (
    <div className={cn("pt-6 pb-4", PADX)}>
      <SectionHeader
        title={title}
        showArrows={false}
        showSeeAll={onSeeAll !== undefined}
        onSeeAll={onSeeAll}
      />
      <div
        className={cn(
          "grid gap-x-[var(--grid-gutter,16px)] gap-y-6",
          COLUMN_GRID[columns]
        )}
      >
        {collections.map((collection, index) => {
          const tileInner = (
            <>
              <div
                className={cn(
                  "bg-fs-surface-secondary w-full overflow-hidden rounded",
                  imageAspect
                    ? IMAGE_ASPECT[imageAspect]
                    : COLUMN_ASPECT[columns]
                )}
              >
                {collection.imageUrl ? (
                  <img
                    src={collection.imageUrl}
                    alt={collection.label}
                    className={cn(
                      "h-full w-full object-cover",
                      TILE_HOVER_IMAGE
                    )}
                  />
                ) : null}
              </div>
              {collection.labelInImage ? null : (
                <span className="type-fs-paragraph-medium text-fs-text-primary text-left">
                  {collection.label}
                </span>
              )}
            </>
          );
          const href = linkFor?.(collection);
          return href ? (
            <a
              key={`${collection.label}-${index}`}
              href={href}
              className="flex w-full flex-col gap-2"
            >
              {tileInner}
            </a>
          ) : (
            <button
              key={`${collection.label}-${index}`}
              data-signup-gate
              className={cn("flex w-full flex-col gap-2", TILE_HOVER_GROUP)}
            >
              {tileInner}
            </button>
          );
        })}
      </div>
    </div>
  );
};
